import { DeleteOutlined, WarningTwoTone } from '@ant-design/icons';
import { productApi } from '@features/products/api/productApi';
import {
  FileFormatter,
  FileFormatterWithError,
  OfferBulkImportApiResponse,
  OffersImportDrawerProps,
} from '@features/products/interfaces/offerBulkImport';
import {
  createErrorCSVFromSubmittedOffers,
  generateAndDownloadCSV,
  mapOfferBulkHeaders,
  offerBulkHeaderMap,
  parseOfferBulkCSVRows,
} from '@features/products/utils/offerBulk';
import { Button, Drawer, Flex, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { ButtonDownloadTemplate } from './ButtonDownloadTemplate';
import {
  EditableOfferRow,
  EditableOffersPreviewTable,
} from './EditableOffersPreviewTable';
import { ErrorsOfferBulkTable } from './ErrorsOfferBulkTable';
import { FinishOfferBulkImport } from './FinishOfferBulkImport';
import { InputOfferBulk } from './InputOfferBulk';

export function OffersImportDrawer({
  open,
  onClose,
  onSuccessFinishImport,
}: OffersImportDrawerProps) {
  const rowIdCounter = useRef(0);
  const [offersBulkCSV, setOffersBulkCSV] = useState<string>('');
  const [offerRows, setOfferRows] = useState<EditableOfferRow[]>([]);
  const [submittedOffers, setSubmittedOffers] = useState<FileFormatter[]>([]);
  const [isManualMode, setIsManualMode] = useState(false);
  const [parsingWarnings, setParsingWarnings] = useState<
    FileFormatterWithError[]
  >([]);
  const [isSubmittingFile, setIsSubmittingFile] = useState(false);
  const [importResult, setImportResult] =
    useState<OfferBulkImportApiResponse | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [fileFeedback, setFileFeedback] = useState<string | null>(null);

  const offerBulkColumns = useMemo(
    () => Array.from(new Set(Object.values(offerBulkHeaderMap))),
    []
  );

  const createEditableRow = useCallback((values: FileFormatter = {}) => {
    rowIdCounter.current += 1;

    return {
      id: `offer-row-${rowIdCounter.current}`,
      values,
    };
  }, []);

  const validOffers = useMemo(
    () =>
      offerRows
        .map(row => row.values)
        .filter(values =>
          Object.values(values).some(value => String(value).trim() !== '')
        ),
    [offerRows]
  );

  const handleClearImport = useCallback(() => {
    setOffersBulkCSV('');
    setOfferRows([]);
    setSubmittedOffers([]);
    setIsManualMode(false);
    setParsingWarnings([]);
    setImportResult(null);
    setRequestError(null);
    setFileFeedback(null);
  }, []);

  const handleStartManualFill = useCallback(() => {
    setOffersBulkCSV('');
    setOfferRows([createEditableRow()]);
    setSubmittedOffers([]);
    setIsManualMode(true);
    setParsingWarnings([]);
    setImportResult(null);
    setRequestError(null);
    setFileFeedback(null);
  }, [createEditableRow]);

  const handleChangeBulk = useCallback(
    (value: string) => {
      if (!value) {
        handleClearImport();
        return;
      }
      setIsManualMode(false);
      setRequestError(null);
      setFileFeedback(null);
      setOffersBulkCSV(value);
    },
    [handleClearImport]
  );

  const handleInputError = useCallback((message: string) => {
    setRequestError(message || null);
  }, []);

  const parseFile = useCallback(() => {
    if (!offersBulkCSV) {
      if (isManualMode) return;

      setOfferRows([]);
      setSubmittedOffers([]);
      setParsingWarnings([]);
      setImportResult(null);
      setRequestError(null);
      setFileFeedback(null);
      return;
    }

    const { rows, errors } = parseOfferBulkCSVRows(offersBulkCSV);
    const mappedRowsRaw = mapOfferBulkHeaders(rows, offerBulkHeaderMap);
    const mappedRows: EditableOfferRow[] = [];

    mappedRowsRaw.forEach(item => {
      if (!Object.keys(item).length) return;
      mappedRows.push(createEditableRow(item));
    });

    setOfferRows(mappedRows);
    setSubmittedOffers([]);
    setParsingWarnings(errors);
    setImportResult(null);
    setRequestError(null);

    if (!rows.length) {
      setFileFeedback(
        'O arquivo foi carregado, mas não possui linhas válidas para pré-visualização.'
      );
      return;
    }

    if (!mappedRows.length) {
      setFileFeedback(
        'O arquivo foi carregado, mas não foi possível gerar a pré-visualização. Verifique se o CSV segue o template esperado.'
      );
      return;
    }

    setFileFeedback(null);
  }, [createEditableRow, isManualMode, offersBulkCSV]);

  useEffect(() => {
    parseFile();
  }, [parseFile]);

  const handleImportFile = useCallback(async () => {
    if (!validOffers.length) return;

    setIsSubmittingFile(true);
    setRequestError(null);
    setSubmittedOffers(validOffers);

    try {
      const result = await productApi.importOffersBulk({
        offers: validOffers,
      });

      setImportResult(result);
      onSuccessFinishImport?.();
    } catch {
      setRequestError(
        'Não foi possível importar as ofertas no momento. Tente novamente.'
      );
      setImportResult(null);
    } finally {
      setIsSubmittingFile(false);
    }
  }, [onSuccessFinishImport, validOffers]);

  const handleChangeCell = useCallback(
    (rowId: string, field: string, value: string) => {
      setOfferRows(previousRows =>
        previousRows.map(row =>
          row.id === rowId
            ? { ...row, values: { ...row.values, [field]: value } }
            : row
        )
      );
    },
    []
  );

  const handleAddRow = useCallback(() => {
    setOfferRows(previousRows => [...previousRows, createEditableRow()]);
  }, [createEditableRow]);

  const handleRemoveRow = useCallback((rowId: string) => {
    setOfferRows(previousRows => previousRows.filter(row => row.id !== rowId));
  }, []);

  const handleDuplicateRow = useCallback(
    (rowId: string) => {
      setOfferRows(previousRows => {
        const rowToDuplicate = previousRows.find(row => row.id === rowId);
        if (!rowToDuplicate) return previousRows;

        return [
          ...previousRows,
          createEditableRow({ ...rowToDuplicate.values }),
        ];
      });
    },
    [createEditableRow]
  );

  const handleExportErrorsCSV = useCallback(() => {
    if (!importResult || !Object.keys(importResult.errors).length) {
      return;
    }

    const csvContent = createErrorCSVFromSubmittedOffers(
      submittedOffers,
      importResult.errors,
      offerBulkColumns
    );
    if (!csvContent) return;

    generateAndDownloadCSV(
      csvContent,
      `${Date.now()}_erros_import_ofertas.csv`
    );
  }, [importResult, offerBulkColumns, submittedOffers]);

  const apiErrorsTable = importResult
    ? Object.entries(importResult.errors).map(([offerIndex, message]) => ({
        line: String(Number(offerIndex) + 1),
        field: 'API',
        message,
      }))
    : [];

  return (
    <Drawer
      title={'Criar ofertas em lote'}
      width={685}
      open={open}
      onClose={onClose}
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          height: '100dvh',
          minHeight: '100dvh',
        },
        body: {
          display: 'flex',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        },
        footer: {
          flexShrink: 0,
        },
      }}
      footer={
        <Flex
          content='flex-end'
          style={{ width: '100%', gap: 12, padding: '12px 0' }}
        >
          <div style={{ flex: 1 }}>
            <FooterPrimaryActions>
              <ButtonDownloadTemplate />
              {!importResult && (
                <Button onClick={handleStartManualFill}>
                  Preencher manualmente
                </Button>
              )}
            </FooterPrimaryActions>
          </div>
          <Button onClick={onClose}>
            {!!importResult ? 'Fechar' : 'Cancelar'}
          </Button>
          {!importResult && (
            <Button
              type='primary'
              loading={isSubmittingFile}
              disabled={!validOffers.length}
              onClick={handleImportFile}
            >
              Salvar
            </Button>
          )}
        </Flex>
      }
    >
      <DrawerBodyContent>
        {importResult ? (
          <>
            <FinishOfferBulkImport
              totalCreated={importResult.totalCreated}
              errorsCount={Object.keys(importResult.errors).length}
            />
            {!!apiErrorsTable.length && (
              <Content>
                <ErrorsOfferBulkTable data={apiErrorsTable} isLoading={false} />
                <Button
                  type='primary'
                  style={{ alignSelf: 'flex-end' }}
                  onClick={handleExportErrorsCSV}
                >
                  <WarningTwoTone />
                  Baixar planilha com erros
                </Button>
              </Content>
            )}
          </>
        ) : (
          <>
            <PreviewContainer>
              {!!offerRows.length && (
                <EditableOffersPreviewTable
                  rows={offerRows}
                  columnsKeys={offerBulkColumns}
                  onCellChange={handleChangeCell}
                  onAddRow={handleAddRow}
                  onRemoveRow={handleRemoveRow}
                  onDuplicateRow={handleDuplicateRow}
                />
              )}
              {!!parsingWarnings.length && (
                <Content>
                  <ErrorsOfferBulkTable
                    data={parsingWarnings.map(item => ({
                      line: String(item.line || '-'),
                      field: item.field,
                      message: item.messageError,
                    }))}
                    isLoading={false}
                  />
                </Content>
              )}
              {!!fileFeedback && (
                <PreviewTableSection>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    Arquivo carregado
                  </Typography.Title>
                  <Typography.Text type='secondary'>
                    {fileFeedback}
                  </Typography.Text>
                </PreviewTableSection>
              )}
            </PreviewContainer>
            {requestError && <ErrorText>{requestError}</ErrorText>}
            {offersBulkCSV && (
              <PreviewRemoveButton onClick={handleClearImport}>
                <DeleteOutlined size={40} /> Apagar arquivo carregado
              </PreviewRemoveButton>
            )}
            {isManualMode && (
              <PreviewRemoveButton onClick={handleClearImport}>
                <DeleteOutlined size={40} /> Reiniciar preenchimento
              </PreviewRemoveButton>
            )}
            {!offersBulkCSV &&
              !offerRows.length &&
              !parsingWarnings.length && (
                <InputOfferBulk
                  onChange={handleChangeBulk}
                  onError={handleInputError}
                />
              )}
          </>
        )}
      </DrawerBodyContent>
    </Drawer>
  );
}

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

export const PreviewContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  width: 100%;
`;

const FooterPrimaryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DrawerBodyContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const PreviewRemoveButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  align-self: flex-end;
  gap: 4px;

  padding: 8px;
  color: #dc4446;

  transition: 0.6s;

  :hover {
    transform: scale(1.1);
  }
`;

const ErrorText = styled.p`
  color: #dc4446;
`;

export const PreviewTableSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);

  .ant-typography {
    color: rgba(255, 255, 255, 0.92);
  }

  .ant-typography-secondary {
    color: rgba(255, 255, 255, 0.7);
  }
`;
